import React, { createContext, useContext, useState, useEffect } from 'react';

// Social Trading Context - Mock version for frontend development
const SocialTradingContext = createContext();

export const useSocialTrading = () => {
  const context = useContext(SocialTradingContext);
  if (!context) {
    throw new Error('useSocialTrading must be used within a SocialTradingProvider');
  }
  return context;
};

export const SocialTradingProvider = ({ children, canisterId, userId }) => {
  const [loading, setLoading] = useState(false);
  const [activeCompetitions, setActiveCompetitions] = useState([]);
  const [userCompetitions, setUserCompetitions] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [userPortfolios, setUserPortfolios] = useState({});
  const [userTrades, setUserTrades] = useState([]);
  const [platformAnalytics, setPlatformAnalytics] = useState(null);
  const [userEngagement, setUserEngagement] = useState(null);

  // Mock competition data
  const mockCompetitions = [
    {
      competition_id: 'daily_traders_cup',
      title: 'Daily Traders Cup',
      description: '24-hour intensive trading competition focusing on short-term strategies',
      competition_type: 'daily',
      status: 'active',
      start_time: Date.now(),
      end_time: Date.now() + 24 * 60 * 60 * 1000,
      participants: [],
      leaderboard: [],
      entry_requirements: {
        min_skill_level: 'Intermediate',
        min_xp: 500,
        completed_tutorials: ['options_basics_101', 'protective_puts_102'],
        max_participants: 50,
        entry_fee_virtual: 100.0,
      },
      trading_rules: {
        starting_capital: 25000.0,
        max_position_size: 5000.0,
        risk_limits: {
          max_daily_loss: 5000.0,
          max_drawdown: 10000.0,
          position_concentration_limit: 0.2,
        },
        allowed_strategies: ['protective_put', 'covered_call', 'speculation'],
        forbidden_actions: ['borrowing', 'leveraged_trading'],
        scoring_method: 'risk_adjusted',
      },
      prize_structure: {
        first_place: {
          xp_reward: 1000,
          badges: ['Daily Trading Champion'],
          virtual_currency: 5000.0,
          special_recognition: 'Featured on leaderboard for 1 week',
          platform_privileges: ['Advanced analytics access'],
        },
        second_place: {
          xp_reward: 600,
          badges: ['Daily Trading Expert'],
          virtual_currency: 3000.0,
          special_recognition: null,
          platform_privileges: [],
        },
        third_place: {
          xp_reward: 400,
          badges: ['Daily Trading Pro'],
          virtual_currency: 1500.0,
          special_recognition: null,
          platform_privileges: [],
        },
        participation_rewards: {
          xp_reward: 50,
          badges: [],
          virtual_currency: 100.0,
          special_recognition: null,
          platform_privileges: [],
        },
      },
      market_scenario: {
        scenario_name: 'High Volatility Day',
        description: 'Simulated high volatility market conditions with multiple price swings',
        initial_btc_price: 105000.0,
        volatility_environment: 'High',
        duration_hours: 24,
      },
    },
    {
      competition_id: 'beginner_options_academy',
      title: 'Beginner Options Academy Cup',
      description: 'Educational competition designed for new options traders - Join instantly and learn through experience!',
      competition_type: 'educational',
      status: 'active',
      start_time: Date.now(),
      end_time: Date.now() + 72 * 60 * 60 * 1000,
      participants: [],
      leaderboard: [],
      entry_requirements: {
        min_skill_level: 'Beginner',
        min_xp: 0,
        completed_tutorials: [],
        max_participants: 100,
        entry_fee_virtual: 0.0,
      },
      trading_rules: {
        starting_capital: 10000.0,
        max_position_size: 2000.0,
        risk_limits: {
          max_daily_loss: 2000.0,
          max_drawdown: 5000.0,
          position_concentration_limit: 0.15,
        },
        allowed_strategies: ['protective_put', 'speculation'],
        forbidden_actions: ['complex_strategies'],
        scoring_method: 'total_return',
      },
      prize_structure: {
        first_place: {
          xp_reward: 800,
          badges: ['Beginner Champion'],
          virtual_currency: 2000.0,
          special_recognition: 'Mentorship session with expert',
          platform_privileges: ['Tutorial fast-track access'],
        },
        second_place: {
          xp_reward: 500,
          badges: ['Fast Learner'],
          virtual_currency: 1200.0,
          special_recognition: null,
          platform_privileges: [],
        },
        third_place: {
          xp_reward: 300,
          badges: ['Rising Star'],
          virtual_currency: 800.0,
          special_recognition: null,
          platform_privileges: [],
        },
        participation_rewards: {
          xp_reward: 100,
          badges: ['Academy Graduate'],
          virtual_currency: 200.0,
          special_recognition: null,
          platform_privileges: [],
        },
      },
      market_scenario: {
        scenario_name: 'Gentle Learning Environment',
        description: 'Stable market conditions ideal for learning basic concepts through hands-on experience',
        initial_btc_price: 105000.0,
        volatility_environment: 'Low',
        duration_hours: 72,
      },
    },
  ];

  // Load active competitions
  const loadActiveCompetitions = async () => {
    try {
      setLoading(true);
      console.log('Loading mock active competitions...');
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      setActiveCompetitions(mockCompetitions);
      console.log('Loaded mock active competitions:', mockCompetitions);
    } catch (error) {
      console.error('Failed to load active competitions:', error);
      setActiveCompetitions([]);
    } finally {
      setLoading(false);
    }
  };

  // Load user competitions and portfolios
  const loadUserCompetitions = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      // For now, just set empty array since we're focusing on basic functionality
      setUserCompetitions([]);
      setUserPortfolios({});
    } catch (error) {
      console.error('Failed to load user competitions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Register for competition
  const registerForCompetition = async (competitionId, displayName, strategyDescription) => {
    if (!userId) return { success: false, error: 'Not authenticated' };
    
    try {
      setLoading(true);
      console.log('Mock registering for competition:', competitionId, displayName);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock successful registration
      const result = {
        success: true,
        registration_confirmed: true,
        assigned_starting_capital: 25000.0,
        error: null,
      };
      
      // Reload user competitions
      await loadUserCompetitions();
      return { success: true, data: result };
    } catch (error) {
      console.error('Failed to register for competition:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Create user profile
  const createUserProfile = async (displayName) => {
    if (!userId) return { success: false, error: 'Not authenticated' };
    
    try {
      const profile = {
        user_id: userId,
        display_name: displayName,
        xp_points: 100, // Starting XP
        completed_tutorials: [],
        skill_level: 'Beginner'
      };
      
      console.log('Mock creating user profile:', profile);
      setUserProfile(profile);
      return { success: true };
    } catch (error) {
      console.error('Failed to create user profile:', error);
      return { success: false, error: error.message };
    }
  };

  // Load user profile
  const loadUserProfile = async () => {
    if (!userId) return;
    
    try {
      // Mock user profile
      const profile = {
        user_id: userId,
        display_name: 'Demo Trader',
        xp_points: 100,
        completed_tutorials: [],
        skill_level: 'Beginner'
      };
      setUserProfile(profile);
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  };

  // Record demo trade for competition
  const recordDemoTrade = async (competitionId, tradeData) => {
    if (!userId) return { success: false, error: 'Not authenticated' };
    
    try {
      console.log('Mock recording demo trade:', competitionId, tradeData);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Update the portfolio with the new trade
      setUserPortfolios(prevPortfolios => {
        const currentPortfolio = prevPortfolios[competitionId] || {
          user_id: userId,
          competition_id: competitionId,
          starting_balance: 25000,
          current_balance: 25000,
          total_pnl: 0,
          total_trades: 0,
          win_rate: 0,
          last_updated: Date.now()
        };
        
        // Calculate new portfolio values based on trade
        const tradeCost = tradeData.premium * tradeData.quantity;
        const newBalance = currentPortfolio.current_balance - tradeCost;
        const newTotalTrades = currentPortfolio.total_trades + 1;
        
        // For demo purposes, simulate some profit/loss
        const simulatedPnL = Math.random() > 0.5 ? tradeCost * 0.1 : -tradeCost * 0.05;
        const newTotalPnL = currentPortfolio.total_pnl + simulatedPnL;
        const newBalanceWithPnL = newBalance + simulatedPnL;
        
        // Calculate win rate (simplified)
        const wins = Math.floor(newTotalTrades * 0.6); // 60% win rate for demo
        const newWinRate = (wins / newTotalTrades) * 100;
        
        const updatedPortfolio = {
          ...currentPortfolio,
          current_balance: newBalanceWithPnL,
          total_pnl: newTotalPnL,
          total_trades: newTotalTrades,
          win_rate: newWinRate,
          last_updated: Date.now()
        };
        
        console.log('Updated portfolio:', updatedPortfolio);
        
        return {
          ...prevPortfolios,
          [competitionId]: updatedPortfolio
        };
      });
      
      return { success: true };
    } catch (error) {
      console.error('Failed to record demo trade:', error);
      return { success: false, error: error.message };
    }
  };

  // Load user trades
  const loadUserTrades = async () => {
    if (!userId) return;
    
    try {
      // For now, just set empty array
      setUserTrades([]);
    } catch (error) {
      console.error('Failed to load user trades:', error);
    }
  };

  // Load platform analytics
  const loadPlatformAnalytics = async () => {
    try {
      // Mock platform analytics
      const analytics = {
        total_users: 1250,
        active_users_today: 89,
        active_users_week: 342,
        total_competitions_run: 15,
        total_trades_executed: 12500,
        average_session_time: 45.5,
        user_retention_rate: 0.78,
        most_popular_competition: 'Beginner Options Academy Cup',
      };
      setPlatformAnalytics(analytics);
    } catch (error) {
      console.error('Failed to load platform analytics:', error);
    }
  };

  // Load user engagement
  const loadUserEngagement = async () => {
    if (!userId) return;
    
    try {
      // Mock user engagement
      const engagement = {
        user_id: userId,
        total_sessions: 5,
        total_time_spent: 1800, // 30 minutes
        competitions_joined: 1,
        competitions_completed: 0,
        total_trades: 12,
        last_active: Date.now(),
        streak_days: 2,
        achievement_points: 150,
        referrals: 0,
      };
      setUserEngagement(engagement);
    } catch (error) {
      console.error('Failed to load user engagement:', error);
    }
  };

  // Track user session
  const trackUserSession = async (sessionDuration) => {
    if (!userId) return;
    
    try {
      console.log('Mock tracking user session:', sessionDuration);
    } catch (error) {
      console.error('Failed to track user session:', error);
    }
  };

  // Get competition leaderboard
  const getCompetitionLeaderboard = async (competitionId) => {
    try {
      // Mock leaderboard
      return [
        { user_id: 'user1', display_name: 'Trader Pro', current_rank: 1, total_return: 15.5, current_portfolio_value: 28750.0 },
        { user_id: 'user2', display_name: 'Options Master', current_rank: 2, total_return: 12.3, current_portfolio_value: 28075.0 },
        { user_id: 'user3', display_name: 'Risk Manager', current_rank: 3, total_return: 8.7, current_portfolio_value: 27175.0 },
      ];
    } catch (error) {
      console.error('Failed to get competition leaderboard:', error);
      return [];
    }
  };

  // Auto-load data when component mounts
  useEffect(() => {
    loadActiveCompetitions();
    loadPlatformAnalytics();
  }, []);

  // Auto-load user data when userId is available
  useEffect(() => {
    if (userId) {
      loadUserProfile();
      loadUserCompetitions();
      loadUserTrades();
      loadUserEngagement();
    }
  }, [userId]);

  const value = {
    // State
    activeCompetitions,
    userCompetitions,
    userProfile,
    userPortfolios,
    userTrades,
    platformAnalytics,
    userEngagement,
    loading,
    
    // Actions
    loadActiveCompetitions,
    loadUserCompetitions,
    registerForCompetition,
    createUserProfile,
    loadUserProfile,
    recordDemoTrade,
    loadUserTrades,
    loadPlatformAnalytics,
    loadUserEngagement,
    trackUserSession,
    getCompetitionLeaderboard,
  };

  return (
    <SocialTradingContext.Provider value={value}>
      {children}
    </SocialTradingContext.Provider>
  );
}; 